import { apiEndpoints } from '@/lib/api/endpoints'
import { baseApi } from '@/lib/store/api/baseQuery'

export type QuestionNeighborsResponse = {
    previous_question_key: string | null
    next_question_key: string | null
    current_question_visible: boolean
}

export type LeaveHiddenQuestionHandlers = {
    buildQuestionUrl: (questionKey: string | number) => string
    persistentQueryString: string
    replaceUrl: (url: string) => void
    onQuestionnaireExhausted: (hashFragment: string) => void | Promise<void>
}

export type GetQuestionNeighborsArg = {
    questionnaireId: string
    questionKey: string
    questionnaireType: 'screen' | 'history'
    skuId?: string
    responseJson?: unknown
    pendingQuestionKey?: string
}

/** Cache-key fragment used to bind leave-hidden navigation handlers for a neighbors request. */
export function questionNeighborsLeaveKey(questionnaireId: string, questionKey: string): string {
    return `${questionnaireId}:${questionKey}`
}

const leaveHiddenHandlersByKey = new Map<string, LeaveHiddenQuestionHandlers>()

/** Registers leave-hidden navigation for the active on-screen conditional question. */
export function bindLeaveHiddenQuestionHandlers(leaveKey: string, handlers: LeaveHiddenQuestionHandlers): void {
    leaveHiddenHandlersByKey.set(leaveKey, handlers)
}

/** Clears leave-hidden handlers when the conditional question unmounts or changes. */
export function unbindLeaveHiddenQuestionHandlers(leaveKey: string): void {
    leaveHiddenHandlersByKey.delete(leaveKey)
}

/** Opens a neighbor question, or exhausts the questionnaire when none remain. */
export function leaveHiddenQuestionFromNeighbors(
    neighbors: QuestionNeighborsResponse,
    handlers: LeaveHiddenQuestionHandlers,
): void | Promise<void> {
    if (neighbors.current_question_visible) return

    const hash = typeof window !== 'undefined' ? window.location.hash : ''
    const openQuestion = (questionKey: string | number) => {
        handlers.replaceUrl(`${handlers.buildQuestionUrl(questionKey)}${handlers.persistentQueryString}${hash}`)
    }

    if (neighbors.previous_question_key) {
        openQuestion(neighbors.previous_question_key)
        return
    }

    if (neighbors.next_question_key) {
        openQuestion(neighbors.next_question_key)
        return
    }

    return handlers.onQuestionnaireExhausted(hash)
}

export const questionsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Read-only neighbors lookup. Core exposes this as POST so the body can
         * carry a pending answer; declared as a mutation because the HTTP method
         * is POST.
         */
        getQuestionNeighbors: builder.mutation<QuestionNeighborsResponse, GetQuestionNeighborsArg>({
            query: ({ questionnaireId, questionKey, questionnaireType, skuId, responseJson, pendingQuestionKey }) => {
                const body: Record<string, unknown> = {
                    questionnaire_type: questionnaireType,
                }
                if (skuId) body.sku_id = skuId
                if (responseJson !== undefined) {
                    body.response_json = responseJson
                    if (pendingQuestionKey && pendingQuestionKey !== questionKey) {
                        body.pending_question_key = pendingQuestionKey
                    }
                }
                return {
                    url: apiEndpoints.questionnaires.getNeighbors(questionnaireId, questionKey),
                    method: 'POST',
                    body,
                }
            },
            async onQueryStarted(arg, { queryFulfilled }) {
                const leaveKey = questionNeighborsLeaveKey(arg.questionnaireId, arg.questionKey)
                try {
                    const { data } = await queryFulfilled
                    const handlers = leaveHiddenHandlersByKey.get(leaveKey)
                    if (!handlers) return
                    await leaveHiddenQuestionFromNeighbors(data, handlers)
                } catch {
                    // Failure is treated as visible by the hook; do not navigate.
                }
            },
        }),
    }),
    overrideExisting: false,
})

export const { useGetQuestionNeighborsMutation } = questionsApi
